import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import EnvConfig from '../utils/config';

// Contexto de SignalR
const SignalRContext = createContext();

// Provider del contexto
export const SignalRProvider = ({ children }) => {
  const [connection, setConnection] = useState(null);
  const [notificaciones, setNotificaciones] = useState([]);
  const [conectado, setConectado] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    intentosConexion: 0,
    ultimoError: null,
    estadoConexion: 'disconnected',
  });

  // Referencia para manejar callbacks
  const dashboardUpdateCallbackRef = useRef(null);
  const pagoCompletadoCallbackRef = useRef(null);
  const monitorCallbackRef = useRef(null); // Callback para actualización de monitor
  const ticketNuevoCallbackRef = useRef(null); // Callback para nuevo ticket
  const ticketActualizadoCallbackRef = useRef(null); // Callback para ticket actualizado
  const mensajeNuevoCallbackRef = useRef(null); // Callback para nuevo mensaje en chat
  const nuevoTicketDBCallbackRef = useRef(null); // Callback para nuevo ticket (DB)
  const nuevoInvitadoCallbackRef = useRef(null); // Callback para nuevo invitado
  const intentosConexionRef = useRef(0);
  const conectandoRef = useRef(false); // Flag para evitar conexiones simultáneas
  const ultimoIntentoRef = useRef(0); // Timestamp del último intento

  // Función para conectar a SignalR
  const conectarSignalR = useCallback(async (userId) => {
    // Verificar si ya está conectado, conectando, o si es muy pronto para otro intento
    const ahora = Date.now();
    const tiempoMinimo = 2000; // 2 segundos entre intentos
    
    if (conectado || conectandoRef.current) {
      if (EnvConfig.DEBUG_MODE) {
        console.log('⚠️ Ya está conectado o conectando a SignalR');
      }
      return { success: true };
    }
    
    if (ahora - ultimoIntentoRef.current < tiempoMinimo) {
      if (EnvConfig.DEBUG_MODE) {
        console.log('⏳ Demasiado pronto para otro intento de conexión');
      }
      return { success: false, error: 'Demasiado pronto para reconectar' };
    }

    // Marcar como conectando y actualizar timestamp
    conectandoRef.current = true;
    ultimoIntentoRef.current = ahora;

    setDebugInfo(prev => ({
      ...prev,
      intentosConexion: prev.intentosConexion + 1
    }));
    
    // Incrementar el ref también
    intentosConexionRef.current = intentosConexionRef.current + 1;

    if (EnvConfig.DEBUG_MODE) {
      console.log(`🔄 Intento de conexión SignalR #${intentosConexionRef.current}`);
    }

    try {
      // Crear nueva conexión SignalR
      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(`https://eventosapi-v2.azurewebsites.net/api/v1?userId=${userId}`)
        .withAutomaticReconnect([0, 20000, 50000, 60000]) // Intervalos más controlados
        .configureLogging(signalR.LogLevel.Information) // Reducir logs
        .build();

      // Eventos de conexión
      newConnection.onreconnecting((error) => {
        if (EnvConfig.DEBUG_MODE) {
          console.log('🔄 Reconectando SignalR...', error);
        }
        setDebugInfo(prev => ({
          ...prev,
          estadoConexion: 'reconnecting'
        }));
      });

      newConnection.onreconnected((connectionId) => {
        if (EnvConfig.DEBUG_MODE) {
          console.log('✅ Reconectado a SignalR:', connectionId);
        }
        setDebugInfo(prev => ({
          ...prev,
          estadoConexion: 'connected'
        }));
      });

      newConnection.onclose((error) => {
        if (EnvConfig.DEBUG_MODE) {
          console.log('🔌 Conexión SignalR cerrada:', error);
        }
        setConectado(false);
        setDebugInfo(prev => ({
          ...prev,
          estadoConexion: 'disconnected',
          ultimoError: error
        }));
      });

      // Escuchar evento principal para actualización del dashboard
      newConnection.on('actualizarDashboard', (data) => {
        if (EnvConfig.DEBUG_MODE) {
          console.log('📩 Notificación recibida: actualizarDashboard');
          console.log('📊 Datos completos:', JSON.stringify(data, null, 2));
        }

        // Agregar notificación al estado
        const nuevaNotificacion = {
          tipo: 'dashboard_update',
          mensaje: 'El dashboard requiere actualización',
          data,
          timestamp: new Date().toISOString(),
        };

        setNotificaciones(prev => [nuevaNotificacion, ...prev]);

        // Ejecutar callback si existe
        if (dashboardUpdateCallbackRef.current) {
          dashboardUpdateCallbackRef.current(data);
        }
      });

      // Escuchar evento de pago completado (intentar ambos formatos)
      const registrarEventoPago = (nombreEvento) => {
        newConnection.on(nombreEvento, (data) => {
          if (EnvConfig.DEBUG_MODE) {
            console.log(`💰 Notificación recibida: ${nombreEvento}`);
            console.log('💳 Datos del pago:', JSON.stringify(data, null, 2));
          }

          // Agregar notificación al estado
          const nuevaNotificacion = {
            tipo: 'pago_completado',
            mensaje: 'Pago completado exitosamente',
            data,
            timestamp: new Date().toISOString(),
          };

          setNotificaciones(prev => [nuevaNotificacion, ...prev]);

          // Ejecutar callback de pago completado si existe (solo si estás en el módulo de Pagos)
          if (pagoCompletadoCallbackRef.current) {
            console.log('🔔 [SignalR] Ejecutando callback de pago completado');
            pagoCompletadoCallbackRef.current(data);
          } else if (EnvConfig.DEBUG_MODE) {
            console.log('ℹ️ [SignalR] Callback de pagos no activo (no estás en el módulo de Pagos)');
          }

          // ✅ También actualizar el dashboard cuando se complete un pago
          if (dashboardUpdateCallbackRef.current) {
            console.log('📊 [SignalR] Actualizando dashboard por pago completado');
            dashboardUpdateCallbackRef.current(data);
          } else if (EnvConfig.DEBUG_MODE) {
            console.log('ℹ️ [SignalR] Callback de dashboard no activo (no estás en el Dashboard)');
          }
        });
      };

      // Registrar ambos formatos del evento
      registrarEventoPago('pagoCompletado');
      registrarEventoPago('pagocompletado');

      // Escuchar evento de nuevo invitado registrado
      newConnection.on('nuevoInvitadoRegistrado', (data) => {
        if (EnvConfig.DEBUG_MODE) {
          console.log('👤 Notificación recibida: nuevoInvitadoRegistrado');
          console.log('👤 Datos del invitado:', JSON.stringify(data, null, 2));
        }

        const nuevaNotificacion = {
          tipo: 'nuevo_invitado',
          mensaje: data.mensaje || 'Nuevo invitado registrado',
          data,
          timestamp: new Date().toISOString(),
        };

        setNotificaciones(prev => [nuevaNotificacion, ...prev]);

        if (nuevoInvitadoCallbackRef.current) {
          nuevoInvitadoCallbackRef.current(data);
        }

        if (dashboardUpdateCallbackRef.current) {
          dashboardUpdateCallbackRef.current(data);
        }
      });

      // Escuchar evento de mesa seleccionada para monitor
      newConnection.on('mesaSeleccionada', (data) => {
        if (EnvConfig.DEBUG_MODE) {
          console.log('🪑 Notificación recibida: mesaSeleccionada');
          console.log('🪑 Datos de la mesa:', JSON.stringify(data, null, 2));
        }

        // Agregar notificación al estado
        const nuevaNotificacion = {
          tipo: 'mesa_seleccionada',
          mensaje: 'Mesa seleccionada por graduado',
          data,
          timestamp: new Date().toISOString(),
        };

        setNotificaciones(prev => [nuevaNotificacion, ...prev]);

        // Ejecutar callback de monitor si existe
        if (monitorCallbackRef.current) {
          console.log('🔔 [SignalR] Ejecutando callback de monitor');
          monitorCallbackRef.current(data);
        } else if (EnvConfig.DEBUG_MODE) {
          console.log('ℹ️ [SignalR] Callback de monitor no activo (no estás en el modo Monitor)');
        }
      });

      // Escuchar evento de mesa bloqueada/desbloqueada para monitor
      newConnection.on('mesaBloqueada', (data) => {
        if (EnvConfig.DEBUG_MODE) {
          console.log('🔒 Notificación recibida: mesaBloqueada');
          console.log('🔒 Datos de la mesa:', JSON.stringify(data, null, 2));
        }

        // Agregar notificación al estado
        const nuevaNotificacion = {
          tipo: 'mesa_bloqueada',
          mensaje: data.bloqueada ? 'Mesa bloqueada' : 'Mesa desbloqueada',
          data,
          timestamp: new Date().toISOString(),
        };

        setNotificaciones(prev => [nuevaNotificacion, ...prev]);

        // Ejecutar callback de monitor si existe (reutilizamos el mismo callback)
        if (monitorCallbackRef.current) {
          console.log('🔔 [SignalR] Ejecutando callback de monitor por cambio de bloqueo');
          monitorCallbackRef.current(data);
        } else if (EnvConfig.DEBUG_MODE) {
          console.log('ℹ️ [SignalR] Callback de monitor no activo (no estás en el modo Monitor)');
        }
      });

      // Escuchar evento de nuevo ticket creado
      newConnection.on('nuevoTicketCreado', (data) => {
        if (EnvConfig.DEBUG_MODE) {
          console.log('🎫 Notificación recibida: nuevoTicket');
          console.log('🎫 Datos del ticket:', JSON.stringify(data, null, 2));
        }

        // Agregar notificación al estado
        const nuevaNotificacion = {
          tipo: 'nuevo_ticket',
          mensaje: 'Nuevo ticket creado',
          data,
          timestamp: new Date().toISOString(),
        };

        setNotificaciones(prev => [nuevaNotificacion, ...prev]);

        // Ejecutar callback si existe
        if (ticketNuevoCallbackRef.current) {
          console.log('🔔 [SignalR] Ejecutando callback de nuevo ticket');
          ticketNuevoCallbackRef.current(data);
        } else if (EnvConfig.DEBUG_MODE) {
          console.log('ℹ️ [SignalR] Callback de nuevo ticket no activo');
        }
      });

      // Escuchar evento de nuevo ticket (DB) - Según requerimiento de usuario
      newConnection.on('nuevoTicket', (notificacion) => {
        if (EnvConfig.DEBUG_MODE) {
          console.log('🔔 Notificación Nuevo Ticket DB:', notificacion);
        }

        // Agregar a la lista de notificaciones local (opcional, el user lo quiere en la campanita)
        const nuevaNotif = {
          tipo: 'NUEVO_TICKET_DB',
          mensaje: notificacion.mensaje,
          data: notificacion,
          timestamp: notificacion.created_at || notificacion.fechaCreacion || new Date().toISOString(),
        };

        setNotificaciones(prev => [nuevaNotif, ...prev]);

        // Ejecutar callback para la campanita
        if (nuevoTicketDBCallbackRef.current) {
          nuevoTicketDBCallbackRef.current(notificacion);
        }

        // Ejecutar también el callback general de nuevo ticket para que la lista se actualice
        if (ticketNuevoCallbackRef.current) {
          console.log('🔔 [SignalR] Notificando actualización de lista por nuevoTicket');
          ticketNuevoCallbackRef.current(notificacion);
        }
      });

      // Escuchar evento de ticket actualizado
      newConnection.on('ticketActualizado', (data) => {
        if (EnvConfig.DEBUG_MODE) {
          console.log('🔄 Notificación recibida: ticketActualizado');
          console.log('🔄 Datos del ticket:', JSON.stringify(data, null, 2));
        }

        // Agregar notificación al estado
        const nuevaNotificacion = {
          tipo: 'ticket_actualizado',
          mensaje: 'Ticket actualizado',
          data,
          timestamp: new Date().toISOString(),
        };

        setNotificaciones(prev => [nuevaNotificacion, ...prev]);

        // Ejecutar callback si existe
        if (ticketActualizadoCallbackRef.current) {
          console.log('🔔 [SignalR] Ejecutando callback de ticket actualizado');
          ticketActualizadoCallbackRef.current(data);
        } else if (EnvConfig.DEBUG_MODE) {
          console.log('ℹ️ [SignalR] Callback de ticket actualizado no activo');
        }
      });

      // Escuchar evento de nuevo mensaje en chat (registrar múltiples variaciones)
      const registrarEventoMensaje = (nombreEvento) => {
        console.log(`📝 [SignalR] Registrando evento de mensaje: ${nombreEvento}`);
        
        newConnection.on(nombreEvento, (data) => {
          console.log(`💬 [SignalR] Notificación recibida: ${nombreEvento}`);
          console.log('💬 [SignalR] Datos del mensaje:', JSON.stringify(data, null, 2));

          // Agregar notificación al estado
          const nuevaNotificacion = {
            tipo: 'nuevo_mensaje',
            mensaje: 'Nuevo mensaje recibido',
            data,
            timestamp: new Date().toISOString(),
          };

          setNotificaciones(prev => [nuevaNotificacion, ...prev]);

          // Ejecutar callback si existe
          if (mensajeNuevoCallbackRef.current) {
            console.log('🔔 [SignalR] Ejecutando callback de nuevo mensaje');
            mensajeNuevoCallbackRef.current(data);
          } else {
            console.log('⚠️ [SignalR] Callback de nuevo mensaje no activo - no hay nadie escuchando');
          }
        });
      };

      // Registrar todas las variaciones posibles del evento de mensaje
      console.log('📋 [SignalR] Registrando variaciones del evento de mensaje...');
      // registrarEventoMensaje('nuevomensajeticket'); // lowercase
      registrarEventoMensaje('nuevoMensajeTicket'); // camelCase
      // registrarEventoMensaje('NuevoMensajeTicket'); // PascalCase
      console.log('✅ [SignalR] Todas las variaciones del evento de mensaje registradas');

      if (EnvConfig.DEBUG_MODE) {
        console.log('🚀 Iniciando conexión SignalR...');
      }

      await newConnection.start();

      setConnection(newConnection);
      setConectado(true);
      setDebugInfo(prev => ({
        ...prev,
        estadoConexion: 'connected'
      }));

      if (EnvConfig.DEBUG_MODE) {
        console.log('✅ Conectado a SignalR exitosamente');
        console.log('🆔 Connection ID:', newConnection.connectionId);
      }

      // Limpiar flag de conectando
      conectandoRef.current = false;
      return { success: true };
    } catch (error) {
      if (EnvConfig.DEBUG_MODE) {
        console.error('❌ Error conectando a SignalR:', error);
      }
      
      setDebugInfo(prev => ({
        ...prev,
        ultimoError: error,
        estadoConexion: 'error'
      }));

      // Limpiar flag de conectando
      conectandoRef.current = false;
      return { success: false, error };
    }
  }, [conectado]); // Eliminar debugInfo.intentosConexion de las dependencias

  // Función para desconectar SignalR
  const desconectarSignalR = useCallback(async () => {
    if (connection && conectado) {
      try {
        await connection.stop();
        setConnection(null);
        setConectado(false);
        setDebugInfo(prev => ({
          ...prev,
          estadoConexion: 'disconnected'
        }));
        
        // Limpiar flag de conectando
        conectandoRef.current = false;
        
        if (EnvConfig.DEBUG_MODE) {
          console.log('🔌 Desconectado de SignalR');
        }
      } catch (error) {
        if (EnvConfig.DEBUG_MODE) {
          console.error('❌ Error al desconectar SignalR:', error);
        }
        // Limpiar flag incluso si hay error
        conectandoRef.current = false;
      }
    }
  }, [connection, conectado]);

  // Función para limpiar notificaciones
  const limpiarNotificaciones = useCallback(() => {
    setNotificaciones([]);
  }, []);

  // Función para registrar callback de actualización del dashboard
  const registrarCallbackDashboard = useCallback((callback) => {
    dashboardUpdateCallbackRef.current = callback;
  }, []);

  // Función para desregistrar callback
  const desregistrarCallbackDashboard = useCallback(() => {
    dashboardUpdateCallbackRef.current = null;
  }, []);

  // Función para registrar callback de pago completado
  const registrarCallbackPagoCompletado = useCallback((callback) => {
    pagoCompletadoCallbackRef.current = callback;
  }, []);

  // Función para desregistrar callback de pago completado
  const desregistrarCallbackPagoCompletado = useCallback(() => {
    pagoCompletadoCallbackRef.current = null;
  }, []);

  // Función para registrar callback de monitor (mesas seleccionadas)
  const registrarCallbackMonitor = useCallback((callback) => {
    monitorCallbackRef.current = callback;
  }, []);

  // Función para desregistrar callback de monitor
  const desregistrarCallbackMonitor = useCallback(() => {
    monitorCallbackRef.current = null;
  }, []);

  // Función para registrar callback de nuevo ticket
  const registrarCallbackNuevoTicket = useCallback((callback) => {
    ticketNuevoCallbackRef.current = callback;
  }, []);

  // Función para desregistrar callback de nuevo ticket
  const desregistrarCallbackNuevoTicket = useCallback(() => {
    ticketNuevoCallbackRef.current = null;
  }, []);

  // Función para registrar callback de ticket actualizado
  const registrarCallbackTicketActualizado = useCallback((callback) => {
    ticketActualizadoCallbackRef.current = callback;
  }, []);

  // Función para desregistrar callback de ticket actualizado
  const desregistrarCallbackTicketActualizado = useCallback(() => {
    ticketActualizadoCallbackRef.current = null;
  }, []);

  // Función para registrar callback de nuevo mensaje
  const registrarCallbackNuevoMensaje = useCallback((callback) => {
    mensajeNuevoCallbackRef.current = callback;
  }, []);

  // Función para desregistrar callback de nuevo mensaje
  const desregistrarCallbackNuevoMensaje = useCallback(() => {
    mensajeNuevoCallbackRef.current = null;
  }, []);

  // Función para registrar callback de nuevo ticket DB
  const registrarCallbackNuevoTicketDB = useCallback((callback) => {
    nuevoTicketDBCallbackRef.current = callback;
  }, []);

  // Función para desregistrar callback de nuevo ticket DB
  const desregistrarCallbackNuevoTicketDB = useCallback(() => {
    nuevoTicketDBCallbackRef.current = null;
  }, []);

  // Función para registrar callback de nuevo invitado
  const registrarCallbackNuevoInvitado = useCallback((callback) => {
    nuevoInvitadoCallbackRef.current = callback;
  }, []);

  // Función para desregistrar callback de nuevo invitado
  const desregistrarCallbackNuevoInvitado = useCallback(() => {
    nuevoInvitadoCallbackRef.current = null;
  }, []);

  // Función para obtener información de estado
  const obtenerEstadoConexion = useCallback(() => {
    return {
      conectado,
      connection,
      debugInfo,
      notificaciones: notificaciones.length
    };
  }, [conectado, connection, debugInfo, notificaciones.length]);

  const value = {
    // Estado
    connection,
    notificaciones,
    conectado,
    debugInfo,
    
    // Funciones
    conectarSignalR,
    desconectarSignalR,
    limpiarNotificaciones,
    registrarCallbackDashboard,
    desregistrarCallbackDashboard,
    registrarCallbackPagoCompletado,
    desregistrarCallbackPagoCompletado,
    registrarCallbackMonitor,
    desregistrarCallbackMonitor,
    registrarCallbackNuevoTicket,
    desregistrarCallbackNuevoTicket,
    registrarCallbackTicketActualizado,
    desregistrarCallbackTicketActualizado,
    registrarCallbackNuevoMensaje,
    desregistrarCallbackNuevoMensaje,
    registrarCallbackNuevoTicketDB,
    desregistrarCallbackNuevoTicketDB,
    registrarCallbackNuevoInvitado,
    desregistrarCallbackNuevoInvitado,
    obtenerEstadoConexion,
  };

  return (
    <SignalRContext.Provider value={value}>
      {children}
    </SignalRContext.Provider>
  );
};

// Hook personalizado para usar el contexto de SignalR
export const useSignalR = () => {
  const context = useContext(SignalRContext);

  if (!context) {
    throw new Error('useSignalR debe ser usado dentro de un SignalRProvider');
  }

  return context;
};

export default SignalRContext;